import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/store';
import { useI18n } from '@/components/I18nProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Building2,
  Eye,
  EyeOff,
  Loader2,
  Shield,
  MessageCircle,
  BarChart3,
  ArrowRight,
  CheckCircle,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const features = [
  { icon: MessageCircle, textKey: 'feature1' },
  { icon: BarChart3, textKey: 'feature2' },
  { icon: Shield, textKey: 'feature3' },
  { icon: Zap, textKey: 'feature1' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    const result = await login(email, password);
    if (result.success) {
      toast.success('Giriş başarılı!');
      const user = useAuthStore.getState().user;
      navigate(user?.role === 'super_admin' ? '/admin' : '/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px'}} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[120px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-emerald-500/8 blur-[100px] translate-y-1/3" />

        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="login-logo-link">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-emerald-400 transition-transform group-hover:scale-105">
              <Building2 className="h-5 w-5" />
            </div>
            <span className="text-xl font-heading font-bold text-white">ÖdemeTakibi</span>
          </Link>

          {/* Main content */}
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl xl:text-4xl font-heading font-bold text-white leading-tight">
                Tahsilat sürecinizi
                <br />
                <span className="text-emerald-400">dijitalleştirin.</span>
              </h1>
              <p className="mt-4 text-base text-slate-400 leading-relaxed max-w-md">
                Ödeme sözlerini takip edin, WhatsApp ile hatırlatma gönderin, tahsilat oranınızı artırın.
              </p>
            </div>

            <div className="space-y-3">
              {features.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 text-sm text-slate-300"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5 text-emerald-400">
                    <f.icon className="h-4 w-4" />
                  </div>
                  {t(f.textKey)}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom testimonial */}
          <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-5">
            <p className="text-sm text-slate-300 leading-relaxed italic">
              "Tahsilat oranımız %40 arttı. WhatsApp entegrasyonu işimizi kolaylaştırdı."
            </p>
            <div className="mt-3 flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-emerald-400/20 flex items-center justify-center text-emerald-400 text-xs font-bold">AY</div>
              <div>
                <p className="text-xs font-medium text-white">Ahmet Yılmaz</p>
                <p className="text-[11px] text-slate-500">Yılmaz Ticaret, Genel Müdür</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex flex-1 items-center justify-center p-6 sm:p-10 bg-background">
        <div className="w-full max-w-[400px] space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white">
              <Building2 className="h-5 w-5" />
            </div>
            <span className="text-xl font-heading font-bold">ÖdemeTakibi</span>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">
              Hoş Geldiniz
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('loginSubtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="ornek@firma.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">{t('password')}</Label>
                <a
                  href="https://wa.me/902166069924?text=Merhaba%2C%20%C5%9Fifre%20s%C4%B1f%C4%B1rlama%20talep%20ediyorum."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                  data-testid="forgot-password-link"
                >
                  <MessageCircle className="h-3 w-3" />
                  Sifremi unuttum
                </a>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 pr-10"
                  data-testid="password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                <p className="text-sm text-destructive" data-testid="error-message">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 text-sm font-semibold"
              disabled={isLoading}
              data-testid="login-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('loggingIn')}
                </>
              ) : (
                <>
                  {t('login')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">veya</span></div>
          </div>

          <div className="text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('dontHaveAccount')}
            </p>
            <Link to="/register" data-testid="register-link">
              <Button variant="outline" className="w-full h-11">
                {t('register')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="flex items-center justify-center gap-4 pt-4 text-[11px] text-muted-foreground">
            <div className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              <span>256-bit SSL</span>
            </div>
            <span className="text-muted-foreground/30">|</span>
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              <span>KVKK Uyumlu</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
